import {
	createFileRoute,
	useNavigate,
	useParams,
	useRouteContext,
} from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import { graphql, useLazyLoadQuery, useMutation } from "react-relay";
import { toast } from "sonner";
import z from "zod";
import { Heading } from "@/components/heading";
import {
	LessonQuizForm,
	type LessonQuizFormSchemaValues,
} from "@/forms/lesson-quiz-form";
import type { cursosAulasNovaQuizCreateMutation } from "./__generated__/cursosAulasNovaQuizCreateMutation.graphql";
import type { cursosAulasNovaQuizQuery } from "./__generated__/cursosAulasNovaQuizQuery.graphql";

export const Route = createFileRoute(
	"/_logged/c/$club/(sidebar)/(admins)/area-de-membros/cursos/$slug/aulas/quiz/novo",
)({
	component: RouteComponent,
	validateSearch: zodValidator(
		z.object({
			sectionId: z.string().optional(),
		}),
	),
});

function RouteComponent() {
	const navigate = useNavigate({ from: Route.fullPath });
	const { slug: courseSlug } = useParams({ from: Route.id });
	const { sectionId } = Route.useSearch();
	const { club } = useRouteContext({ from: Route.id });

	const data = useLazyLoadQuery<cursosAulasNovaQuizQuery>(
		graphql`
			query cursosAulasNovaQuizQuery($clubSlug: String!, $courseSlug: String!) {
				club(slug: $clubSlug) {
					course(slug: $courseSlug) {
						id
						sections(status: ALL) {
							id
							name
						}
					}
				}
			}
		`,
		{ clubSlug: club.slug, courseSlug },
	);

	const [createCourseQuiz] = useMutation<cursosAulasNovaQuizCreateMutation>(
		graphql`
			mutation cursosAulasNovaQuizCreateMutation($payload: CreateCourseQuizInput!) {
				courseQuizCreate(courseQuizCreateInput: $payload) {
					id
					course {
						id
						sections(status: ALL) {
							id
							name
							classes(status: ALL) {
								id
								name
								slug
								active
								position
							}
						}
					}
				}
			}
		`,
	);

	const sections =
		data.club.course.sections.map((section) => ({
			id: section.id,
			name: section.name,
		})) ?? [];

	const handleQuizSubmit = (values: LessonQuizFormSchemaValues) => {
		return new Promise<void>((resolve, reject) => {
			createCourseQuiz({
				variables: {
					payload: {
						club: club.slug,
						course: data.club.course.id,
						section: values.sectionId,
						name: values.name,
						description: values.description || "",
						quizType: values.quizType,
						questionCount: values.questionCount,
						questions: values.questions.map((q, qIndex) => ({
							title: q.title,
							description: q.description || "",
							position: qIndex,
							active: true,
							options: q.options.map((opt, optIndex) => ({
								text: opt.text,
								isAnswer: opt.isAnswer,
								position: optIndex,
								active: true,
							})),
						})),
						active: true,
					},
				},
				onCompleted: (_response, errors) => {
					if (errors?.length) {
						toast.error(errors[0].message);
						reject(new Error(errors[0].message));
						return;
					}
					toast.success("Quiz criado com sucesso!");
					navigate({
						to: "/c/$club/area-de-membros/cursos/$slug",
						params: { club: club.slug, slug: courseSlug },
					});
					resolve();
				},
				onError: (error) => {
					console.error("Error creating quiz:", error);
					toast.error("Erro ao criar quiz. Tente novamente.");
					reject(error);
				},
			});
		});
	};

	return (
		<div className="size-full flex items-start justify-center py-4 md:py-8">
			<div className="p-6 w-full max-md:px-4 max-w-[700px] flex flex-col gap-6 border rounded-md">
				<Heading>Criar Quiz</Heading>

				<LessonQuizForm
					sections={sections}
					initialSectionId={sectionId}
					onSubmit={handleQuizSubmit}
				/>
			</div>
		</div>
	);
}
